import React, { useEffect, useState } from 'react';
import { Button, Input, Space, Typography } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';

import { useCreateTicket } from './store';
import { SLOT_TYPES } from './constants';
import {
    getFormattedDate,
    noSlotInMedicalEscalation,
} from 'modules/utilities/utility';
import useAssignedStore from 'modules/my_tickets/assigned_tickets/store';
import { CATEGORY } from 'modules/my_tickets/data';

const { Text } = Typography;

const CreateTicketFooter = ({
    ticketPayload,
    validation,
    slotBookingPayload,
    subcategoryOptions = [],
    escalation,
    bypassedCombination,
    ticketData = [],
    escalateTicket,
    onSlotBookingFailed
}) => {
    const { createTicket, ticketLoading, ticketResponse } = useCreateTicket();
    const { updateAssignedData, updateLoading } = useAssignedStore();
    const [slotTime, setSlotTime] = useState(null);

    useEffect(() => {
        const slotTime = _.get(ticketResponse, ['medical_slot_time'], null);
        const time = slotTime ? getFormattedDate(slotTime) : slotTime;
        setSlotTime(time);
    }, [ticketResponse]);

    const onCreateTicket = async () => {
        const payload = { ...ticketPayload };

        // Check if subcategory has slot configuration
        const currentSubcat = subcategoryOptions.find(
            (s) => s.value === ticketPayload.sub_category
        );

        const hasSlot = currentSubcat?.has_slot;
        const slotType = currentSubcat?.slot_type;

        // Check if inline transfer toggle is visible
        const isInlineTransferVisible =
            hasSlot &&
            slotType === SLOT_TYPES.MEDICAL_ESCALATION &&
            !noSlotInMedicalEscalation(ticketPayload.sub_category);

        const isInlineTransfer = ticketPayload.inline_transfer === true;

        if (isInlineTransferVisible && !isInlineTransfer) {
            if (
                !ticketPayload.inline_input_comment ||
                ticketPayload.inline_input_comment.trim().length < 1
            ) {
                toast.error('Please add reason for slot booking');
                return;
            }
        }

        if (!isInlineTransfer) {
            if (hasSlot && slotType) {
                payload.slotBooking = slotBookingPayload;

                const slotTime = _.get(payload, ['slotBooking', 'slotTime'], false);

                if (slotTime && moment(slotTime).isBefore(moment())) {
                    toast.error('This slot has passed. Please select new slot.');
                    return;
                }

                payload.slotType = slotType;
            }

            if (
                hasSlot &&
                !noSlotInMedicalEscalation(ticketPayload.sub_category)
            ) {
                if (!slotBookingPayload.slotTime) {
                    toast.error('Please select a slot for this ticket');
                    return;
                }
            }
        }

        const result = await createTicket(payload);
        if (result && !result.success && result.status === 422) {
            onSlotBookingFailed?.(result.message);
        }
    };

    const onEscalateExisting = async () => {
        try {
            // Find all matching tickets (same category/subcategory within 15 days)
            const { category, sub_category } = bypassedCombination;
            const isMedicalEscalation = category === CATEGORY.MEDICAL_ESCALATIONS;

            const matchingTickets = ticketData.filter((ticket) => {
                const isWithin15Days = moment(ticket.updatedAt).isAfter(
                    moment().subtract(15, 'days').startOf('day')
                );
                if (!isWithin15Days) return false;

                if (isMedicalEscalation) {
                    return ticket.category === CATEGORY.MEDICAL_ESCALATIONS &&
                           (!sub_category || ticket.sub_category === sub_category);
                } else {
                    return ticket.category === category &&
                           ticket.sub_category === sub_category;
                }
            });

            if (matchingTickets.length === 0) {
                toast.error('Could not find the ticket to escalate');
                return;
            }

            // Priority 1: Find ticket with Open status
            let existingTicket = matchingTickets.find((ticket) => ticket.status === 'Open');

            // Priority 2: If no Open ticket, get most recently updated
            if (!existingTicket) {
                existingTicket = matchingTickets.sort((a, b) =>
                    moment(b.updatedAt).diff(moment(a.updatedAt))
                )[0];
            }

            // Build payload only with required changes
            const escalatePayload = {
                escalation: true,
            };

            // Only add status fields if ticket is not already Open
            if (existingTicket.status !== 'Open') {
                escalatePayload.status = 'Open';
                escalatePayload.status_code = 1;
            }

            // Call the Zustand store action and wait for completion
            await updateAssignedData(existingTicket._id, escalatePayload);
        } catch (error) {
            const errorMessage = _.get(
                error,
                ['response', 'data', 'message'],
                error.message || 'Failed to escalate the ticket'
            );
            toast.error(errorMessage);
        }
    };

    const failingValidations = Object.entries(validation).filter(([, value]) => !value).map(([key]) => key);
    const isButtonDisabled = ticketLoading ||
        (validation && _.size(Object.values(validation)) === 0) ||
        _.size(failingValidations);

    // Debug: log validation state when button is disabled
    if (isButtonDisabled && !ticketLoading) {
        console.log('[CreateTicket] Button disabled. Validation state:', JSON.stringify(validation), 'Failing:', failingValidations);
    }

    return (
        <div className="bottom-0 border border-1 p-3">
            {escalation ? (
                <div className="d-flex justify-flex-between align-items-center gap-4">
                    <Button
                        onClick={escalateTicket}
                    >
                        Escalate
                    </Button>
                </div>
            ) : (
                <div className={bypassedCombination ? 'd-flex flex-column gap-3' : 'd-flex justify-flex-between align-items-center gap-4'}>
                    <div className="d-flex gap-2">
                        <Button
                            disabled={isButtonDisabled}
                            onClick={onCreateTicket}
                        >
                            Create Ticket
                        </Button>

                        {/* Show "Escalate Existing" button when user has bypassed escalation */}
                        {bypassedCombination && (
                            <Button
                                onClick={onEscalateExisting}
                                loading={updateLoading}
                            >
                                Escalate Existing
                            </Button>
                        )}
                    </div>

                    {/* Show slot status for subcategories that have slots */}
                    {(() => {
                        const currentSubcat = subcategoryOptions.find(
                            (s) => s.value === ticketPayload.sub_category
                        );

                        const hasSlot = currentSubcat?.has_slot;
                        const slotType = currentSubcat?.slot_type;

                        return (
                            hasSlot && (
                                <Space.Compact block className={bypassedCombination ? 'my-0' : 'my-3'}>
                  <span
                      style={{
                          background: '#FAFAFA',
                          border: '#D9D9D9',
                          width: '30%',
                      }}
                      className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                  >
                    <Text>
                      {slotType === SLOT_TYPES.MEDICAL_ESCALATION &&
                      ticketPayload.inline_transfer
                          ? 'Inline Transfer'
                          : 'Slot Booked'}
                    </Text>
                  </span>

                                    <Input
                                        style={{ width: '70%' }}
                                        value={
                                            slotType === SLOT_TYPES.MEDICAL_ESCALATION &&
                                            ticketPayload.inline_transfer
                                                ? 'Enabled - Direct Assignment'
                                                : slotTime
                                        }
                                    />
                                </Space.Compact>
                            )
                        );
                    })()}
                </div>
            )}
        </div>
    );
};

export default CreateTicketFooter;
